import { createSlice } from "@reduxjs/toolkit";
import type { ProductSliceTypes } from "../../types/product/product.model";
import { axiosClient } from "../../configs/axiosClient";
import { API } from "../../configs/endpoints";
import { addAsyncCases, createApiThunk } from "../createApiThunk";
import type {
  CreateProductResponse,
  GetProductResponse,
  GetProductsResponse,
  UpdateProductResponse,
} from "../../types/product/product.response";
import type {
  CreateProductPayload,
  GetProductsPayload,
  UpdateProductPayload,
} from "../../types/product/product.payload";

const initialState: ProductSliceTypes = {
  loading: {
    list: false,
    detail: false,
    create: false,
    update: false,
    delete: false,
  },
  error: null,
  product: null,
  products: [],
  pagination: null,
  productVariant: null,
};

export const createProduct = createApiThunk<
  CreateProductResponse,
  CreateProductPayload
>(
  "product/createProduct",
  async (data) => (await axiosClient.post(API.product.create, data)).data,
  "Tạo sản phẩm thất bại",
);

export const updateProduct = createApiThunk<
  UpdateProductResponse,
  UpdateProductPayload
>(
  "product/updateProduct",
  async ({ id, data }) =>
    (await axiosClient.put(API.product.update(id), data)).data,
  "Sửa sản phẩm thất bại",
);

export const deleteProduct = createApiThunk<string, string>(
  "product/deleteProduct",
  async (id) => (await axiosClient.delete(API.product.delete(id))).data,
  "Xóa sản phẩm thất bại",
);

export const getProducts = createApiThunk<
  GetProductsResponse,
  GetProductsPayload
>(
  "product/getProducts",
  async (query) =>
    (await axiosClient.get(API.product.list, { params: query })).data,
  "Lấy danh sách sản phẩm thất bại",
);

export const getProduct = createApiThunk<GetProductResponse, string>(
  "product/getProduct",
  async (id) => (await axiosClient.get(API.product.detail(id))).data,
  "Lấy thông tin sản phẩm thất bại",
);

const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    addAsyncCases({
      builder,
      thunk: createProduct,
      loadingKey: "create",
      fallbackError: "Tạo sản phẩm thất bại",
      onSuccess: (state, payload) => {
        state.products.push(payload.product);
      },
    });
    addAsyncCases({
      builder,
      thunk: updateProduct,
      loadingKey: "update",
      fallbackError: "Sửa sản phẩm thất bại",
      onSuccess: (state, payload) => {
        const updated = payload.product;
        const index = state.products.findIndex((p) => p._id === updated._id);
        if (index !== -1) state.products[index] = updated;
      },
    });
    addAsyncCases({
      builder,
      thunk: deleteProduct,
      loadingKey: "delete",
      fallbackError: "Xóa sản phẩm thất bại",
      onSuccess: (state, payload) => {
        state.products = state.products.filter((p) => p._id !== payload);
      },
    });
    addAsyncCases({
      builder,
      thunk: getProducts,
      loadingKey: "list",
      fallbackError: "Lấy danh sách sản phẩm thất bại",
      onSuccess: (state, payload) => {
        state.products = payload.products;
        state.pagination = payload.pagination;
      },
    });
    addAsyncCases({
      builder,
      thunk: getProduct,
      loadingKey: "detail",
      fallbackError: "Lấy thông tin sản phẩm thất bại",
      onSuccess: (state, payload) => {
        state.productVariant = payload.product;
      },
    });
  },
});

export default productSlice.reducer;
